/**
 * Guia → HTML no formato editorial, em páginas A4 na horizontal (vem do ManualMaker,
 * dashboard.js): Capa → Sumário (com 5 ou mais passos) → um passo por página (texto à
 * esquerda, captura à direita) → Fecho. É o mesmo ficheiro para ver no browser e para
 * imprimir em PDF (uma página por secção). Cores, letra, logótipo e nome da empresa vêm
 * do kit de marca. Todo o texto é escapado; imagens e letras só embutidas (data:).
 */
import { PRODUCT } from '../brand';
import { DEFAULT_BRAND, FONT_STACKS, type GuideBrand } from '../brand-kit';
import { cleanUrl } from '../api';
import { displayUrl, stepNumbers, stepTitle, type Guide } from '../steps';
import { guideLocale, guideText } from '../guide-text';
import { editorialPalette, pad2, TOC_MIN_STEPS } from './editorial';
import { esc } from './html';

const safeSrc = (u: string | null | undefined) =>
  u && /^data:(image|font)\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]+$/i.test(u) ? u : null;

export interface EditorialHtmlOptions {
  now?: Date;
  /** name = nome da empresa no cabeçalho e na capa. */
  brand?: Partial<GuideBrand>;
}

/** images: id do passo → data URL (já com zoom e marcador). */
export function buildEditorialHtml(
  guide: Pick<Guide, 'title' | 'description' | 'steps' | 'language'>,
  images: Record<string, string | null>,
  o: EditorialHtmlOptions = {},
): string {
  const now = o.now ?? new Date();
  const brand: GuideBrand = { ...DEFAULT_BRAND, ...o.brand };
  const P = editorialPalette(brand.accent, brand.coverColor, brand.highlightColor);
  const c = (hex: string) => '#' + hex;
  const L = guideLocale(guide);
  const t = guideText(L);
  const title = guide.title.trim() || t.guide;
  const company = (brand.name ?? '').trim() || 'Nolens ' + PRODUCT.name;
  const date = now.toLocaleDateString(L);
  const numbers = stepNumbers(guide);
  const N = numbers.size;
  const hasToc = N >= TOC_MIN_STEPS;
  const total = 1 + (hasToc ? 1 : 0) + guide.steps.length + 1;
  const nPass = t.steps(N);
  const logo = safeSrc(brand.logo);
  const fontUrl = brand.font === 'custom' ? safeSrc(brand.fontUrl) : null;
  const font = brand.font === 'custom' && !fontUrl ? FONT_STACKS.inter : FONT_STACKS[brand.font];
  const pg = (n: number) => pad2(n) + ' / ' + pad2(total);
  const hdr = (page: string, label: string) =>
    `<header class="hd"><span>${esc(company)}</span><span>${esc(t.guide)}</span>` +
    `<span class="hd-t">${esc(label)}</span><span class="hd-p">${page}</span></header>`;

  const out: string[] = [];
  // Capa
  out.push(
    `<section class="pg dark cover">${hdr('', '')}` +
      `<div class="cv-body"><h1>${esc(title)}</h1><i class="rule hl"></i><p class="cv-date">${date}</p>` +
      (guide.description.trim() ? `<p class="cv-desc">${esc(guide.description.trim())}</p>` : '') +
      `</div><aside class="col">` +
      (logo ? `<img class="logo" src="${logo}" alt="${esc(company)}"/>` : '') +
      `<b>${pad2(N)}</b><span>${esc(t.stepsWord(N))}</span></aside></section>`,
  );
  // Sumário
  if (hasToc) {
    const items = guide.steps
      .filter((s) => s.kind !== 'text')
      .map((s) => `<li><span class="n">${pad2(numbers.get(s.id)!)}</span>${esc(stepTitle(s, L))}</li>`)
      .join('');
    out.push(
      `<section class="pg toc">${hdr(pg(2), title)}` +
        `<div class="toc-side"><h2>${esc(t.contents)}</h2><i class="rule"></i><p>${nPass}</p></div>` +
        `<ol>${items}</ol></section>`,
    );
  }
  // Passos e blocos de texto
  guide.steps.forEach((s, i) => {
    const page = pg(i + 2 + (hasToc ? 1 : 0));
    if (s.kind === 'text') {
      const tone = s.tone || 'info';
      const head = s.title.trim() || t[tone];
      const body = s.description.trim() ? `<p class="desc">${esc(s.description.trim())}</p>` : '';
      out.push(
        tone === 'heading'
          ? `<section class="pg sect">${hdr(page, title)}<div><h2>${esc(head)}</h2><i class="rule"></i>${body}</div></section>`
          : `<section class="pg note ${tone}">${hdr(page, title)}<div><p class="tone">${esc(t[tone])}</p>` +
              `<i class="rule sm"></i><h2>${esc(head)}</h2>${body}</div></section>`,
      );
      return;
    }
    const n = pad2(numbers.get(s.id) ?? i + 1);
    const st = stepTitle(s, L);
    const img = safeSrc(images[s.id]);
    const href = cleanUrl(s.url);
    const url =
      (s.kind === 'navigate' || s.kind === 'tab') && href && !st.includes(displayUrl(s.url))
        ? `<p class="url"><a href="${esc(href)}">${esc(displayUrl(s.url))}</a></p>`
        : '';
    out.push(
      `<section class="pg step${img ? '' : ' noimg'}">${hdr(page, title)}` +
        `<div class="st-text"><span class="num">${n}</span><i class="rule sm"></i><h2>${esc(st)}</h2>` +
        (s.description.trim() ? `<p class="desc">${esc(s.description.trim())}</p>` : '') +
        url +
        `</div>` +
        (img ? `<figure><img src="${img}" alt="${esc(st)}"/></figure>` : '') +
        `</section>`,
    );
  });
  // Fecho
  out.push(
    `<section class="pg dark close">${hdr('', '')}` +
      `<div class="cv-body"><h1>${esc(t.endOfGuide)}</h1><i class="rule hl"></i>` +
      `<p class="cl-t">${esc(title)}</p><p class="cv-date">${nPass} · ${date}</p>` +
      (brand.footer.trim() ? `<p class="cv-desc">${esc(brand.footer.trim())}</p>` : '') +
      `</div><aside class="col">${logo ? `<img class="logo" src="${logo}" alt="${esc(company)}"/>` : ''}</aside></section>`,
  );

  const css =
    (fontUrl
      ? `@font-face{font-family:'BrandFont';src:url(${fontUrl}) format('woff2');font-display:block}`
      : '') +
    `:root{--dark:${c(P.dark)};--on-dark:${c(P.onDark)};--col:${c(P.column)};--on-col:${c(P.onColumn)};` +
    `--hl:${c(P.highlight)};--num:${c(P.number)};--paper:${c(P.paper)};--ink:${c(P.ink)}}` +
    '*{box-sizing:border-box;margin:0;padding:0}html{background:var(--dark)}' +
    `body{font-family:${font};font-weight:400;color:var(--ink);background:var(--dark);padding:24px 16px}` +
    'h1,h2{font-weight:400}' +
    // Página: A4 horizontal; cresce se o conteúdo precisar
    '.pg{position:relative;container-type:inline-size;width:100%;max-width:1120px;aspect-ratio:297/210;margin:0 auto 24px;' +
    'background:var(--paper);padding:9cqw 5cqw 4.5cqw;display:grid;gap:3cqw;overflow:hidden}' +
    '.dark{background:var(--dark);color:var(--on-dark)}' +
    '.hd{position:absolute;left:5cqw;right:5cqw;top:3.2cqw;display:grid;grid-template-columns:20cqw 15cqw 1fr auto;' +
    'font-size:max(9px,.85cqw);letter-spacing:.12em;text-transform:uppercase}' +
    '.hd span{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding-right:2cqw}.hd-p{padding-right:0!important}' +
    '.cover .hd,.close .hd{right:27.5%}' +
    '.rule{display:block;width:6cqw;height:max(2px,.45cqw);background:var(--col)}.rule.hl{background:var(--hl)}.rule.sm{width:3.6cqw}' +
    // Capa e fecho
    '.cover,.close{grid-template-columns:1fr 22.5%;padding-right:0}' +
    '.cv-body{align-self:end;padding-bottom:9cqw;padding-right:4cqw;display:flex;flex-direction:column;gap:2cqw}' +
    '.cv-body h1{font-size:max(28px,4.6cqw);line-height:1.12;overflow-wrap:anywhere}' +
    '.cv-date{color:var(--hl);font-size:max(13px,1.4cqw)}.cv-desc{font-size:max(12px,1.15cqw);opacity:.85}' +
    '.cl-t{font-size:max(14px,1.6cqw)}' +
    '.col{position:absolute;right:0;top:0;bottom:0;width:22.5%;background:var(--col);color:var(--on-col);' +
    'display:flex;flex-direction:column;justify-content:flex-end;padding:0 3cqw 9cqw}' +
    '.col .logo{position:absolute;left:3cqw;top:3cqw;max-width:calc(100% - 6cqw);max-height:6cqw;object-fit:contain}' +
    '.col b{font-weight:400;font-size:max(36px,6cqw);line-height:1}' +
    '.col span{font-size:max(9px,.9cqw);letter-spacing:.14em;text-transform:uppercase;margin-top:1cqw}' +
    // Sumário
    '.toc{grid-template-columns:30% 1fr;align-content:start}.toc-side{display:flex;flex-direction:column;gap:1.6cqw}' +
    '.toc h2{font-size:max(22px,2.8cqw)}.toc-side p{font-size:max(12px,1.2cqw)}' +
    '.toc ol{list-style:none;columns:2;column-gap:3cqw;font-size:max(12px,1.2cqw);line-height:1.35}' +
    '.toc li{break-inside:avoid;margin-bottom:.9cqw;display:flex;gap:1.6cqw}.toc .n{color:var(--num);min-width:2.6cqw}' +
    // Passo: texto à esquerda, captura à direita (proporção real)
    '.step{grid-template-columns:31% 1fr;align-items:start}.step.noimg{grid-template-columns:1fr}' +
    '.st-text{display:flex;flex-direction:column;gap:1.6cqw}' +
    '.num{color:var(--num);font-size:max(30px,5cqw);line-height:1}' +
    '.step h2{font-size:max(17px,2.1cqw);line-height:1.25;overflow-wrap:anywhere}' +
    '.desc{font-size:max(13px,1.3cqw);line-height:1.5;white-space:pre-wrap}' +
    '.url{font-size:max(10px,.9cqw);word-break:break-all}.url a{color:var(--num);text-decoration:none}' +
    'figure{display:flex;justify-content:center;align-items:flex-start;min-width:0}' +
    'figure img{display:block;max-width:100%;max-height:52cqw;width:auto;height:auto;border:1px solid rgba(0,0,0,.14)}' +
    // Blocos de texto e secções
    '.note,.sect{align-content:center}.note>div,.sect>div{display:flex;flex-direction:column;gap:1.6cqw;max-width:75cqw}' +
    '.tone{color:var(--num);font-size:max(10px,1cqw);letter-spacing:.14em;text-transform:uppercase}' +
    '.note h2{font-size:max(22px,2.8cqw);line-height:1.2}.sect h2{font-size:max(28px,4cqw);line-height:1.15}' +
    // Telemóvel: uma coluna
    '@media screen and (max-width:700px){.step,.toc{grid-template-columns:1fr}.toc ol{columns:1}figure img{max-height:none}' +
    '.pg{padding-top:12cqw;padding-bottom:8cqw;aspect-ratio:auto;min-height:60cqw}.cover,.close{min-height:120cqw}' +
    '.hd{grid-template-columns:1fr auto}.hd span:nth-child(2),.hd-t{display:none}}' +
    // Impressão / PDF: uma página A4 horizontal por secção
    '@page{size:A4 landscape;margin:0}' +
    '@media print{html,body{background:none;padding:0}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
    '.pg{width:297mm;max-width:none;height:210mm;aspect-ratio:auto;margin:0;break-after:page}.pg:last-child{break-after:auto}}';

  return (
    '<!DOCTYPE html><html lang="' + L + '"><head><meta charset="UTF-8"/>' +
    '<meta name="viewport" content="width=device-width,initial-scale=1"/>' +
    `<title>${esc(title)}</title><style>${css}</style></head><body>` +
    out.join('') +
    '</body></html>'
  );
}
